import React, { useState, useEffect } from 'react';
import gisService from '@/modules/utility/gis/services/gisService';
import SearchInput from '@/components/base/SearchInput';
import { IconPicker } from '@/lib/dreamstep-sdk/ui';
import LayerThemeEditor from '@/components/LayerThemeEditor';
import { ListSkeleton, TableSkeleton } from '@/components/base/Skeleton';

interface LayerGroup {
  id: number;
  group_code: string;
  group_name: string;
  group_name_local: string;
  description: string;
  icon: string;
  display_order: number;
  sequence_id: number;
  is_active: boolean;
}

interface FeatureLayer {
  id: number;
  layer_code: string;
  layer_name: string;
  layer_name_local: string | null;
  description: string | null;
  table_name: string;
  geometry_column: string;
  geometry_type: string;
  filter_column: string | null;
  filter_sql: string | null;
  default_filter_value: string | null;
  default_style: any;
  name_column: string | null;
  additional_columns: string[] | null;
  is_clustered: boolean;
  cluster_distance: number;
  vector_tile_enabled: boolean;
  tile_min_zoom: number;
  tile_max_zoom: number;
  tile_buffer: number;
  tile_extent: number;
  tile_simplify_level: number;
  tile_attributes: string[] | null;
  min_zoom: number;
  max_zoom: number;
  icon: string;
  layer_order: number;
  sequence_id: number;
  layer_group_id: number | null;
  data_source_id: number | null;
  is_active: boolean;
  group_code?: string;
  group_name?: string;
  group_icon?: string;
  data_source_name?: string;
  data_source_type?: string;
  created_at?: string;
  updated_at?: string;
}

interface DataSource {
  id: number;
  source_name: string;
  source_type: string;
  description: string;
  is_active: boolean;
}

interface Table {
  table_schema: string;
  table_name: string;
  table_type: string;
  column_count: number;
}

interface Column {
  column_name: string;
  data_type: string;
  udt_name: string;
  is_nullable: string;
  column_default: string | null;
  ordinal_position: number;
  is_geometry: boolean;
}

const Layers: React.FC = () => {
  // State
  const [layers, setLayers] = useState<FeatureLayer[]>([]);
  const [layerGroups, setLayerGroups] = useState<LayerGroup[]>([]);
  const [dataSources, setDataSources] = useState<DataSource[]>([]);
  const [tables, setTables] = useState<Table[]>([]);
  const [columns, setColumns] = useState<Column[]>([]);
  const [isLoadingTables, setIsLoadingTables] = useState(false);
  const [isLoadingColumns, setIsLoadingColumns] = useState(false);
  const [tablePreview, setTablePreview] = useState<any>(null);
  const [isLoadingPreview, setIsLoadingPreview] = useState(false);
  const [showPreview, setShowPreview] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGroup, setSelectedGroup] = useState<number | null>(null);
  const [selectedGroupFilter, setSelectedGroupFilter] = useState<number | null>(null);
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [geometryFilter, setGeometryFilter] = useState<string>('all');
  const [vectorTileFilter, setVectorTileFilter] = useState<'all' | 'vector' | 'leaflet'>('all');
  const [sortBy, setSortBy] = useState<keyof FeatureLayer>('layer_name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingLayer, setEditingLayer] = useState<FeatureLayer | null>(null);
  const [deletingLayer, setDeletingLayer] = useState<FeatureLayer | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showThemeEditor, setShowThemeEditor] = useState(false);
  const [editingThemeLayer, setEditingThemeLayer] = useState<FeatureLayer | null>(null);

  // Form state
  const [formData, setFormData] = useState({
    layer_code: '',
    layer_name: '',
    layer_name_local: '',
    description: '',
    table_name: '',
    geometry_column: 'geom',
    geometry_type: 'Geometry',
    filter_column: '',
    filter_sql: '',
    default_filter_value: '',
    name_column: '',
    additional_columns: '',
    is_clustered: false,
    cluster_distance: 50,
    vector_tile_enabled: true,
    tile_min_zoom: 0,
    tile_max_zoom: 22,
    tile_buffer: 64,
    tile_extent: 4096,
    tile_simplify_level: 0.5,
    tile_attributes: '',
    min_zoom: 0,
    max_zoom: 22,
    icon: 'ri-map-pin-line',
    layer_order: 0,
    sequence_id: 0,
    layer_group_id: null as number | null,
    data_source_id: 6 as number | null,
    is_active: true
  });

  const itemsPerPage = 10;
  const geometryTypes = ['Point', 'LineString', 'Polygon', 'MultiPoint', 'MultiLineString', 'MultiPolygon', 'Geometry'];

  // Fetch data
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const [layersResponse, groupsResponse, dataSourcesResponse] = await Promise.all([
        gisService.getAllFeatureLayers(),
        gisService.getAllLayerGroups(),
        gisService.getAllDataSources({ is_active: true })
      ]);

      console.log('Layers Response:', layersResponse);
      console.log('Groups Response:', groupsResponse);
      console.log('Data Sources Response:', dataSourcesResponse);

      if (layersResponse.status === 200 && layersResponse.data) {
        setLayers(layersResponse.data);
      }

      if (groupsResponse.status === 200 && groupsResponse.data) {
        setLayerGroups(groupsResponse.data);
      }

      if (dataSourcesResponse.status === 200 && dataSourcesResponse.data) {
        setDataSources(dataSourcesResponse.data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch tables when data source changes
  const fetchTables = async (dataSourceId: number) => {
    try {
      setIsLoadingTables(true);
      setTables([]);
      setColumns([]);

      const response = await gisService.get(`/apiv1/gis/data-sources/${dataSourceId}/tables`);

      if (response.status === 200 && response.data?.data) {
        setTables(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching tables:', error);
    } finally {
      setIsLoadingTables(false);
    }
  };

  // Fetch columns when table changes
  const fetchColumns = async (dataSourceId: number, tableName: string) => {
    try {
      setIsLoadingColumns(true);
      setColumns([]);

      // Encode table name to handle schema.table format
      const encodedTableName = encodeURIComponent(tableName);
      const response = await gisService.get(`/apiv1/gis/data-sources/${dataSourceId}/tables/${encodedTableName}/columns`);

      if (response.status === 200 && response.data?.data) {
        setColumns(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching columns:', error);
    } finally {
      setIsLoadingColumns(false);
    }
  };

  // Fetch table preview when table changes
  const fetchTablePreview = async (dataSourceId: number, tableName: string) => {
    try {
      setIsLoadingPreview(true);
      setTablePreview(null);

      // Encode table name to handle schema.table format
      const encodedTableName = encodeURIComponent(tableName);
      const response = await gisService.get(`/apiv1/gis/data-sources/${dataSourceId}/tables/${encodedTableName}/preview`);

      if (response.status === 200 && response.data?.data) {
        setTablePreview(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching table preview:', error);
      setTablePreview(null);
    } finally {
      setIsLoadingPreview(false);
    }
  };

  // Fetch tables when data_source_id changes in form
  useEffect(() => {
    if (formData.data_source_id && (showCreateModal || editingLayer)) {
      fetchTables(formData.data_source_id);
    }
  }, [formData.data_source_id, showCreateModal, editingLayer]);

  // Fetch columns when table_name changes in form
  useEffect(() => {
    if (formData.data_source_id && formData.table_name && (showCreateModal || editingLayer)) {
      fetchColumns(formData.data_source_id, formData.table_name);
      fetchTablePreview(formData.data_source_id, formData.table_name);
    } else {
      setTablePreview(null);
    }
  }, [formData.table_name, formData.data_source_id, showCreateModal, editingLayer]);

  // Calculate group statistics
  const getGroupStats = (groupId: number) => {
    const groupLayers = layers.filter(l => l.layer_group_id === groupId);
    return {
      total: groupLayers.length,
      active: groupLayers.filter(l => l.is_active).length,
      inactive: groupLayers.filter(l => !l.is_active).length
    };
  };

  // Filter and sort layers
  const filteredLayers = layers.filter(layer => {
    const matchesSearch = layer.layer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         layer.layer_code.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         layer.table_name.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesGroup = selectedGroupFilter === null || layer.layer_group_id === selectedGroupFilter;

    const matchesStatus = statusFilter === 'all' ||
                         (statusFilter === 'active' && layer.is_active) ||
                         (statusFilter === 'inactive' && !layer.is_active);

    const matchesGeometry = geometryFilter === 'all' ||
                           (geometryFilter === 'point' && (layer.geometry_type === 'Point' || layer.geometry_type === 'MultiPoint')) ||
                           (geometryFilter === 'linestring' && (layer.geometry_type === 'LineString' || layer.geometry_type === 'MultiLineString')) ||
                           (geometryFilter === 'polygon' && (layer.geometry_type === 'Polygon' || layer.geometry_type === 'MultiPolygon')) ||
                           layer.geometry_type === geometryFilter;

    const matchesVectorTile = vectorTileFilter === 'all' ||
                             (vectorTileFilter === 'vector' && layer.vector_tile_enabled) ||
                             (vectorTileFilter === 'leaflet' && !layer.vector_tile_enabled);

    return matchesSearch && matchesGroup && matchesStatus && matchesGeometry && matchesVectorTile;
  });

  const sortedLayers = [...filteredLayers].sort((a, b) => {
    const aValue = a[sortBy];
    const bValue = b[sortBy];

    if (aValue === null || aValue === undefined) return 1;
    if (bValue === null || bValue === undefined) return -1;

    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortOrder === 'asc'
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }

    if (typeof aValue === 'number' && typeof bValue === 'number') {
      return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
    }

    return 0;
  });

  const totalPages = Math.ceil(sortedLayers.length / itemsPerPage);
  const paginatedLayers = sortedLayers.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  // Handlers
  const handleSort = (column: keyof FeatureLayer) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const resetForm = () => {
    setFormData({
      layer_code: '',
      layer_name: '',
      layer_name_local: '',
      description: '',
      table_name: '',
      geometry_column: 'geom',
      geometry_type: 'Geometry',
      filter_column: '',
      filter_sql: '',
      default_filter_value: '',
      name_column: '',
      additional_columns: '',
      is_clustered: false,
      cluster_distance: 50,
      vector_tile_enabled: true,
      tile_min_zoom: 0,
      tile_max_zoom: 22,
      tile_buffer: 64,
      tile_extent: 4096,
      tile_simplify_level: 0.5,
      tile_attributes: '',
      min_zoom: 0,
      max_zoom: 22,
      icon: 'ri-map-pin-line',
      layer_order: 0,
      sequence_id: 0,
      layer_group_id: null,
      data_source_id: 6,
      is_active: true
    });
    setEditingLayer(null);
    setIsFullscreen(false);
  };

  const handleEdit = (layer: FeatureLayer) => {
    setEditingLayer(layer);
    setFormData({
      layer_code: layer.layer_code,
      layer_name: layer.layer_name,
      layer_name_local: layer.layer_name_local || '',
      description: layer.description || '',
      table_name: layer.table_name,
      geometry_column: layer.geometry_column,
      geometry_type: layer.geometry_type,
      filter_column: layer.filter_column || '',
      filter_sql: layer.filter_sql || '',
      default_filter_value: layer.default_filter_value || '',
      name_column: layer.name_column || '',
      additional_columns: Array.isArray(layer.additional_columns) ? layer.additional_columns.join(', ') : (layer.additional_columns || ''),
      is_clustered: layer.is_clustered,
      cluster_distance: layer.cluster_distance,
      vector_tile_enabled: layer.vector_tile_enabled,
      tile_min_zoom: layer.tile_min_zoom,
      tile_max_zoom: layer.tile_max_zoom,
      tile_buffer: layer.tile_buffer,
      tile_extent: layer.tile_extent,
      tile_simplify_level: layer.tile_simplify_level,
      tile_attributes: Array.isArray(layer.tile_attributes) ? layer.tile_attributes.join(', ') : (layer.tile_attributes || ''),
      min_zoom: layer.min_zoom,
      max_zoom: layer.max_zoom,
      icon: layer.icon,
      layer_order: layer.layer_order,
      sequence_id: layer.sequence_id,
      layer_group_id: layer.layer_group_id,
      data_source_id: layer.data_source_id,
      is_active: layer.is_active
    });
    setShowCreateModal(true);
  };

  const handleDelete = (layer: FeatureLayer) => {
    setDeletingLayer(layer);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deletingLayer) return;

    try {
      await gisService.deleteFeatureLayer(deletingLayer.id);
      await fetchData();
      setShowDeleteModal(false);
      setDeletingLayer(null);
    } catch (error) {
      console.error('Error deleting layer:', error);
      alert('Failed to delete layer');
    }
  };

  const handleEditTheme = (layer: FeatureLayer) => {
    setEditingThemeLayer(layer);
    setShowThemeEditor(true);
  };

  const handleSaveTheme = async (layerId: number, styleConfig: any) => {
    try {
      await gisService.updateFeatureLayer(layerId, {
        default_style: styleConfig
      });
      await fetchData();
    } catch (error) {
      console.error('Error saving theme:', error);
      throw error;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      const layerData = {
        ...formData,
        additional_columns: formData.additional_columns
          ? formData.additional_columns.split(',').map(s => s.trim()).filter(Boolean)
          : null,
        tile_attributes: formData.tile_attributes
          ? formData.tile_attributes.split(',').map(s => s.trim()).filter(Boolean)
          : null
      };

      if (editingLayer) {
        await gisService.updateFeatureLayer(editingLayer.id, layerData);
      } else {
        await gisService.createFeatureLayer(layerData);
      }

      await fetchData();
      setShowCreateModal(false);
      resetForm();
    } catch (error) {
      console.error('Error saving layer:', error);
      alert('Failed to save layer');
    }
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">GIS Layers</h1>
        <p className="text-gray-600 dark:text-gray-400">Manage feature layers organized by groups</p>
      </div>

      {/* Group Cards */}
      {!isLoading && layerGroups.length > 0 && (
        <div className="flex flex-wrap gap-3 mb-6">
          {/* All Layers Card */}
          <div
            onClick={() => setSelectedGroupFilter(null)}
            className={`bg-white dark:bg-gray-800 p-3 rounded-lg border-2 transition-all cursor-pointer flex-shrink-0 w-64 ${
              selectedGroupFilter === null
                ? 'border-blue-500 shadow-lg'
                : 'border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-700'
            }`}
          >
            <div className="flex items-center justify-between">
              {/* Left: Icon + Label */}
              <div className="flex items-center flex-1 min-w-0">
                <div className="w-9 h-9 bg-blue-100 dark:bg-blue-900/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-stack-line text-lg text-blue-600 dark:text-blue-400"></i>
                </div>
                <div className="ml-2 min-w-0 flex-1">
                  <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">All Layers</p>
                  <div className="flex items-center text-xs text-gray-600 dark:text-gray-400 space-x-2 mt-0.5">
                    <span className="flex items-center">
                      <span className="w-1.5 h-1.5 bg-green-500 rounded-full mr-1"></span>
                      {layers.filter(l => l.is_active).length}
                    </span>
                    <span className="flex items-center">
                      <span className="w-1.5 h-1.5 bg-red-500 rounded-full mr-1"></span>
                      {layers.filter(l => !l.is_active).length}
                    </span>
                  </div>
                </div>
              </div>

              {/* Right: Total Count */}
              <div className="text-right ml-3 flex-shrink-0">
                <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{layers.length}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">layers</p>
              </div>
            </div>
          </div>

          {/* Group Cards */}
          {layerGroups.map(group => {
            const stats = getGroupStats(group.id);
            return (
              <div
                key={group.id}
                onClick={() => setSelectedGroupFilter(group.id)}
                className={`bg-white dark:bg-gray-800 p-3 rounded-lg border-2 transition-all cursor-pointer flex-shrink-0 w-64 ${
                  selectedGroupFilter === group.id
                    ? 'border-green-500 shadow-lg'
                    : 'border-gray-200 dark:border-gray-700 hover:border-green-300 dark:hover:border-green-700'
                }`}
              >
                <div className="flex items-center justify-between">
                  {/* Left: Icon + Label */}
                  <div className="flex items-center flex-1 min-w-0">
                    <div className="w-9 h-9 bg-green-100 dark:bg-green-900/20 rounded-lg flex items-center justify-center flex-shrink-0">
                      <i className={`${group.icon} text-lg text-green-600 dark:text-green-400`}></i>
                    </div>
                    <div className="ml-2 min-w-0 flex-1">
                      <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">{group.group_name}</p>
                      <div className="flex items-center text-xs text-gray-600 dark:text-gray-400 space-x-2 mt-0.5">
                        <span className="flex items-center">
                          <span className="w-1.5 h-1.5 bg-green-500 rounded-full mr-1"></span>
                          {stats.active}
                        </span>
                        {stats.inactive > 0 && (
                          <span className="flex items-center">
                            <span className="w-1.5 h-1.5 bg-red-500 rounded-full mr-1"></span>
                            {stats.inactive}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Right: Total Count */}
                  <div className="text-right ml-3 flex-shrink-0">
                    <p className="text-2xl font-bold text-green-600 dark:text-green-400">{stats.total}</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">layers</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Summary Cards */}
      {!isLoading && layers.length > 0 && (
        <div className="flex flex-wrap gap-3 mb-6">
          {/* Vector Layers Card */}
          <div
            onClick={() => setVectorTileFilter(vectorTileFilter === 'vector' ? 'all' : 'vector')}
            className={`bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 p-3 rounded-lg border-2 transition-all cursor-pointer flex-shrink-0 w-64 ${
              vectorTileFilter === 'vector'
                ? 'border-purple-500 shadow-lg ring-2 ring-purple-300 dark:ring-purple-600'
                : 'border-purple-200 dark:border-purple-700 hover:border-purple-400 dark:hover:border-purple-500'
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center flex-1 min-w-0">
                <div className="w-9 h-9 bg-purple-500 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-stack-line text-lg text-white"></i>
                </div>
                <div className="ml-2 min-w-0 flex-1">
                  <p className="text-sm font-semibold text-purple-900 dark:text-purple-100 truncate">Vector Tile Layers</p>
                  <p className="text-xs text-purple-700 dark:text-purple-300 mt-0.5">MVT enabled layers</p>
                </div>
              </div>
              <div className="text-right ml-3 flex-shrink-0">
                <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                  {layers.filter(l => l.vector_tile_enabled).length}
                </p>
                <p className="text-xs text-purple-600 dark:text-purple-400">layers</p>
              </div>
            </div>
          </div>

          {/* Leaflet Layers Card */}
          <div
            onClick={() => setVectorTileFilter(vectorTileFilter === 'leaflet' ? 'all' : 'leaflet')}
            className={`bg-gradient-to-br from-teal-50 to-teal-100 dark:from-teal-900/20 dark:to-teal-800/20 p-3 rounded-lg border-2 transition-all cursor-pointer flex-shrink-0 w-64 ${
              vectorTileFilter === 'leaflet'
                ? 'border-teal-500 shadow-lg ring-2 ring-teal-300 dark:ring-teal-600'
                : 'border-teal-200 dark:border-teal-700 hover:border-teal-400 dark:hover:border-teal-500'
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center flex-1 min-w-0">
                <div className="w-9 h-9 bg-teal-500 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-map-2-line text-lg text-white"></i>
                </div>
                <div className="ml-2 min-w-0 flex-1">
                  <p className="text-sm font-semibold text-teal-900 dark:text-teal-100 truncate">Leaflet Layers</p>
                  <p className="text-xs text-teal-700 dark:text-teal-300 mt-0.5">GeoJSON renderable</p>
                </div>
              </div>
              <div className="text-right ml-3 flex-shrink-0">
                <p className="text-2xl font-bold text-teal-600 dark:text-teal-400">
                  {layers.filter(l => !l.vector_tile_enabled).length}
                </p>
                <p className="text-xs text-teal-600 dark:text-teal-400">layers</p>
              </div>
            </div>
          </div>

          {/* Point Layers Card */}
          <div
            onClick={() => setGeometryFilter(geometryFilter === 'point' ? 'all' : 'point')}
            className={`bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-900/20 dark:to-orange-800/20 p-3 rounded-lg border-2 transition-all cursor-pointer flex-shrink-0 w-64 ${
              geometryFilter === 'point'
                ? 'border-orange-500 shadow-lg ring-2 ring-orange-300 dark:ring-orange-600'
                : 'border-orange-200 dark:border-orange-700 hover:border-orange-400 dark:hover:border-orange-500'
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center flex-1 min-w-0">
                <div className="w-9 h-9 bg-orange-500 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-map-pin-line text-lg text-white"></i>
                </div>
                <div className="ml-2 min-w-0 flex-1">
                  <p className="text-sm font-semibold text-orange-900 dark:text-orange-100 truncate">Point Layers</p>
                  <p className="text-xs text-orange-700 dark:text-orange-300 mt-0.5">Point geometries</p>
                </div>
              </div>
              <div className="text-right ml-3 flex-shrink-0">
                <p className="text-2xl font-bold text-orange-600 dark:text-orange-400">
                  {layers.filter(l => l.geometry_type === 'Point' || l.geometry_type === 'MultiPoint').length}
                </p>
                <p className="text-xs text-orange-600 dark:text-orange-400">layers</p>
              </div>
            </div>
          </div>

          {/* Polyline Layers Card */}
          <div
            onClick={() => setGeometryFilter(geometryFilter === 'linestring' ? 'all' : 'linestring')}
            className={`bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 p-3 rounded-lg border-2 transition-all cursor-pointer flex-shrink-0 w-64 ${
              geometryFilter === 'linestring'
                ? 'border-blue-500 shadow-lg ring-2 ring-blue-300 dark:ring-blue-600'
                : 'border-blue-200 dark:border-blue-700 hover:border-blue-400 dark:hover:border-blue-500'
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center flex-1 min-w-0">
                <div className="w-9 h-9 bg-blue-500 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-route-line text-lg text-white"></i>
                </div>
                <div className="ml-2 min-w-0 flex-1">
                  <p className="text-sm font-semibold text-blue-900 dark:text-blue-100 truncate">Polyline Layers</p>
                  <p className="text-xs text-blue-700 dark:text-blue-300 mt-0.5">LineString geometries</p>
                </div>
              </div>
              <div className="text-right ml-3 flex-shrink-0">
                <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                  {layers.filter(l => l.geometry_type === 'LineString' || l.geometry_type === 'MultiLineString').length}
                </p>
                <p className="text-xs text-blue-600 dark:text-blue-400">layers</p>
              </div>
            </div>
          </div>

          {/* Polygon Layers Card */}
          <div
            onClick={() => setGeometryFilter(geometryFilter === 'polygon' ? 'all' : 'polygon')}
            className={`bg-gradient-to-br from-emerald-50 to-emerald-100 dark:from-emerald-900/20 dark:to-emerald-800/20 p-3 rounded-lg border-2 transition-all cursor-pointer flex-shrink-0 w-64 ${
              geometryFilter === 'polygon'
                ? 'border-emerald-500 shadow-lg ring-2 ring-emerald-300 dark:ring-emerald-600'
                : 'border-emerald-200 dark:border-emerald-700 hover:border-emerald-400 dark:hover:border-emerald-500'
            }`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center flex-1 min-w-0">
                <div className="w-9 h-9 bg-emerald-500 rounded-lg flex items-center justify-center flex-shrink-0">
                  <i className="ri-shape-line text-lg text-white"></i>
                </div>
                <div className="ml-2 min-w-0 flex-1">
                  <p className="text-sm font-semibold text-emerald-900 dark:text-emerald-100 truncate">Polygon Layers</p>
                  <p className="text-xs text-emerald-700 dark:text-emerald-300 mt-0.5">Polygon geometries</p>
                </div>
              </div>
              <div className="text-right ml-3 flex-shrink-0">
                <p className="text-2xl font-bold text-emerald-600 dark:text-emerald-400">
                  {layers.filter(l => l.geometry_type === 'Polygon' || l.geometry_type === 'MultiPolygon').length}
                </p>
                <p className="text-xs text-emerald-600 dark:text-emerald-400">layers</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Search */}
          <div>
            <SearchInput
              value={searchTerm}
              onChange={setSearchTerm}
              placeholder="Search layers..."
            />
          </div>

          {/* Status Filter */}
          <div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as 'all' | 'active' | 'inactive')}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            >
              <option value="all">All Status</option>
              <option value="active">Active Only</option>
              <option value="inactive">Inactive Only</option>
            </select>
          </div>

          {/* Geometry Type Filter */}
          <div>
            <select
              value={geometryFilter}
              onChange={(e) => setGeometryFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            >
              <option value="all">All Geometry Types</option>
              <option value="point">Point Layers</option>
              <option value="linestring">Polyline Layers</option>
              <option value="polygon">Polygon Layers</option>
            </select>
          </div>

          {/* Create Button */}
          <div>
            <button
              onClick={() => { resetForm(); setShowCreateModal(true); }}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center"
            >
              <i className="ri-add-line mr-2"></i>
              Create Layer
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th
                  onClick={() => handleSort('layer_name')}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                >
                  <div className="flex items-center">
                    Layer Name
                    {sortBy === 'layer_name' && (
                      <i className={`ml-1 ri-arrow-${sortOrder === 'asc' ? 'up' : 'down'}-s-line`}></i>
                    )}
                  </div>
                </th>
                <th
                  onClick={() => handleSort('layer_code')}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                >
                  <div className="flex items-center">
                    Code
                    {sortBy === 'layer_code' && (
                      <i className={`ml-1 ri-arrow-${sortOrder === 'asc' ? 'up' : 'down'}-s-line`}></i>
                    )}
                  </div>
                </th>
                <th
                  onClick={() => handleSort('table_name')}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                >
                  <div className="flex items-center">
                    Table Name
                    {sortBy === 'table_name' && (
                      <i className={`ml-1 ri-arrow-${sortOrder === 'asc' ? 'up' : 'down'}-s-line`}></i>
                    )}
                  </div>
                </th>
                <th
                  onClick={() => handleSort('geometry_type')}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                >
                  <div className="flex items-center">
                    Type
                    {sortBy === 'geometry_type' && (
                      <i className={`ml-1 ri-arrow-${sortOrder === 'asc' ? 'up' : 'down'}-s-line`}></i>
                    )}
                  </div>
                </th>
                <th
                  onClick={() => handleSort('layer_group_id')}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                >
                  <div className="flex items-center">
                    Group
                    {sortBy === 'layer_group_id' && (
                      <i className={`ml-1 ri-arrow-${sortOrder === 'asc' ? 'up' : 'down'}-s-line`}></i>
                    )}
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Data Source
                </th>
                <th
                  onClick={() => handleSort('is_active')}
                  className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-600"
                >
                  <div className="flex items-center">
                    Status
                    {sortBy === 'is_active' && (
                      <i className={`ml-1 ri-arrow-${sortOrder === 'asc' ? 'up' : 'down'}-s-line`}></i>
                    )}
                  </div>
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {isLoading ? (
                <tr>
                  <td colSpan={7} className="px-6 py-12 text-center">
                    <ListSkeleton />
                  </td>
                </tr>
              ) : paginatedLayers.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-6 py-16">
                    <div className="flex flex-col items-center justify-center text-center">
                      <div className="w-16 h-16 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mb-4">
                        <i className="ri-stack-line text-3xl text-gray-400 dark:text-gray-500"></i>
                      </div>
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                        No layers found
                      </h3>
                      <p className="text-sm text-gray-500 dark:text-gray-400 mb-4 max-w-sm">
                        {sortedLayers.length === 0 && layers.length > 0
                          ? 'No layers match your current filters. Try adjusting your search or group selection.'
                          : 'Get started by creating your first GIS layer.'}
                      </p>
                      <button
                        onClick={() => { resetForm(); setShowCreateModal(true); }}
                        className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        <i className="ri-add-line mr-2"></i>
                        Create New Layer
                      </button>
                    </div>
                  </td>
                </tr>
              ) : (
                paginatedLayers.map((layer) => (
                  <tr key={layer.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <i className={`${layer.icon} text-xl text-gray-400 mr-3`}></i>
                        <div>
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {layer.layer_name}
                          </div>
                          {layer.description && (
                            <div className="text-xs text-gray-500 dark:text-gray-400 truncate max-w-xs">
                              {layer.description}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400">
                        {layer.layer_code}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">
                      {layer.table_name}
                    </td>
                    <td className="px-6 py-4">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800 dark:bg-purple-900/20 dark:text-purple-400">
                        {layer.geometry_type}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">
                      {layer.group_name || '-'}
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">
                      {layer.data_source_name || '-'}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        layer.is_active
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400'
                          : 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400'
                      }`}>
                        <span className={`w-1.5 h-1.5 rounded-full mr-1.5 ${layer.is_active ? 'bg-green-500' : 'bg-red-500'}`}></span>
                        {layer.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => handleEditTheme(layer)}
                          className="text-purple-600 hover:text-purple-900 dark:text-purple-400 dark:hover:text-purple-300"
                          title="Edit Theme"
                        >
                          <i className="ri-palette-line text-lg"></i>
                        </button>
                        <button
                          onClick={() => handleEdit(layer)}
                          className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                          title="Edit"
                        >
                          <i className="ri-edit-line text-lg"></i>
                        </button>
                        <button
                          onClick={() => handleDelete(layer)}
                          className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                          title="Delete"
                        >
                          <i className="ri-delete-bin-line text-lg"></i>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-gray-50 dark:bg-gray-700 px-6 py-3 flex items-center justify-between border-t border-gray-200 dark:border-gray-600">
            <div className="text-sm text-gray-700 dark:text-gray-300">
              Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, sortedLayers.length)} of {sortedLayers.length} results
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center ${isFullscreen ? 'z-[9999] p-0' : 'z-50 p-4'}`}>
          <div className={`bg-white dark:bg-gray-800 shadow-xl overflow-y-auto ${
            isFullscreen
              ? 'fixed inset-0 w-full h-full rounded-none z-[9999]'
              : 'max-w-4xl w-full max-h-[90vh] rounded-lg'
          }`}>
            <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 flex items-center justify-between z-10">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                {editingLayer ? 'Edit Layer' : 'Create New Layer'}
              </h2>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setIsFullscreen(!isFullscreen)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
                >
                  <i className={`text-2xl ${isFullscreen ? 'ri-fullscreen-exit-line' : 'ri-fullscreen-line'}`}></i>
                </button>
                <button
                  onClick={() => { setShowCreateModal(false); resetForm(); setIsFullscreen(false); }}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  title="Close"
                >
                  <i className="ri-close-line text-2xl"></i>
                </button>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="p-6">
              <div className="space-y-6">
                {/* Basic Information */}
                <div>
                  <div className="flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 rounded-lg mb-4 border-l-4 border-blue-500">
                    <div className="flex items-center justify-center w-10 h-10 bg-blue-500 rounded-lg">
                      <i className="ri-information-line text-xl text-white"></i>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Basic Information</h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Layer Code *
                      </label>
                      <input
                        type="text"
                        value={formData.layer_code}
                        onChange={(e) => setFormData({ ...formData, layer_code: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                        required
                        disabled={!!editingLayer}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Layer Name *
                      </label>
                      <input
                        type="text"
                        value={formData.layer_name}
                        onChange={(e) => setFormData({ ...formData, layer_name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Local Name
                      </label>
                      <input
                        type="text"
                        value={formData.layer_name_local}
                        onChange={(e) => setFormData({ ...formData, layer_name_local: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Layer Group
                      </label>
                      <select
                        value={formData.layer_group_id || ''}
                        onChange={(e) => setFormData({ ...formData, layer_group_id: e.target.value ? parseInt(e.target.value) : null })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      >
                        <option value="">No Group</option>
                        {layerGroups.map(group => (
                          <option key={group.id} value={group.id}>
                            {group.group_name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Description
                      </label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      />
                    </div>
                  </div>
                </div>

                {/* Data Source */}
                <div>
                  <div className="flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 rounded-lg mb-4 border-l-4 border-green-500">
                    <div className="flex items-center justify-center w-10 h-10 bg-green-500 rounded-lg">
                      <i className="ri-database-2-line text-xl text-white"></i>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Data Source</h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Data Source *
                      </label>
                      <select
                        value={formData.data_source_id || ''}
                        onChange={(e) => setFormData({ ...formData, data_source_id: e.target.value ? parseInt(e.target.value) : null })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                        required
                      >
                        <option value="">Select Data Source</option>
                        {dataSources.map(source => (
                          <option key={source.id} value={source.id}>
                            {source.source_name} ({source.source_type})
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Table Name *
                      </label>
                      <select
                        value={formData.table_name}
                        onChange={(e) => setFormData({ ...formData, table_name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                        required
                        disabled={!formData.data_source_id || isLoadingTables}
                      >
                        <option value="">
                          {isLoadingTables ? 'Loading tables...' : formData.data_source_id ? 'Select Table' : 'Select Data Source First'}
                        </option>
                        {tables.map(table => (
                          <option key={`${table.table_schema}.${table.table_name}`} value={`${table.table_schema}.${table.table_name}`}>
                            {table.table_schema}.{table.table_name} ({table.column_count} columns)
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Geometry Column *
                      </label>
                      <select
                        value={formData.geometry_column}
                        onChange={(e) => setFormData({ ...formData, geometry_column: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                        required
                        disabled={!formData.table_name || isLoadingColumns}
                      >
                        <option value="">
                          {isLoadingColumns ? 'Loading columns...' : formData.table_name ? 'Select Geometry Column' : 'Select Table First'}
                        </option>
                        {columns.filter(col => col.is_geometry).map(column => (
                          <option key={column.column_name} value={column.column_name}>
                            {column.column_name} ({column.udt_name})
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Geometry Type *
                      </label>
                      <select
                        value={formData.geometry_type}
                        onChange={(e) => setFormData({ ...formData, geometry_type: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                        required
                      >
                        {geometryTypes.map(type => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Name Column
                      </label>
                      <select
                        value={formData.name_column || ''}
                        onChange={(e) => setFormData({ ...formData, name_column: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                        disabled={!formData.table_name || isLoadingColumns}
                      >
                        <option value="">
                          {isLoadingColumns ? 'Loading columns...' : formData.table_name ? 'Select Name Column' : 'Select Table First'}
                        </option>
                        {columns.filter(col => !col.is_geometry).map(column => (
                          <option key={column.column_name} value={column.column_name}>
                            {column.column_name} ({column.data_type})
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {/* Table Preview */}
                  {formData.table_name && (
                    <div className="mt-4">
                      <div className="flex items-center justify-between mb-2">
                        <button
                          type="button"
                          onClick={() => setShowPreview(!showPreview)}
                          className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                        >
                          <i className={`ri-${showPreview ? 'arrow-down' : 'arrow-right'}-s-line text-lg`}></i>
                          <span>Table Data Preview</span>
                          {tablePreview && (
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              ({tablePreview.totalRows} rows)
                            </span>
                          )}
                        </button>
                      </div>

                      {showPreview && (
                        <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                          {isLoadingPreview ? (
                            <div className="p-4"><TableSkeleton rows={4} columns={5} showToolbar={false} /></div>
                          ) : tablePreview && tablePreview.rows && tablePreview.rows.length > 0 ? (
                            <div className="overflow-x-auto max-h-96">
                              <table className="w-full text-sm">
                                <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0">
                                  <tr>
                                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-600">
                                      #
                                    </th>
                                    {tablePreview.columns.map((col: any) => (
                                      <th
                                        key={col.column_name}
                                        className="px-3 py-2 text-left text-xs font-medium text-gray-700 dark:text-gray-300 border-b border-gray-200 dark:border-gray-600"
                                      >
                                        <div className="flex flex-col">
                                          <span>{col.column_name}</span>
                                          <span className="text-xs font-normal text-gray-500 dark:text-gray-400">
                                            {col.data_type}
                                          </span>
                                        </div>
                                      </th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody className="bg-white dark:bg-gray-800">
                                  {tablePreview.rows.map((row: any, rowIndex: number) => (
                                    <tr
                                      key={rowIndex}
                                      className="border-b border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50"
                                    >
                                      <td className="px-3 py-2 text-gray-500 dark:text-gray-400 font-mono text-xs">
                                        {rowIndex + 1}
                                      </td>
                                      {tablePreview.columns.map((col: any) => (
                                        <td
                                          key={col.column_name}
                                          className="px-3 py-2 text-gray-700 dark:text-gray-300 max-w-xs truncate"
                                          title={row[col.column_name] !== null ? String(row[col.column_name]) : 'NULL'}
                                        >
                                          {row[col.column_name] !== null ? (
                                            col.udt_name === 'geometry' || col.data_type === 'USER-DEFINED' ? (
                                              <span className="text-xs text-blue-600 dark:text-blue-400 font-mono">
                                                &lt;geometry&gt;
                                              </span>
                                            ) : typeof row[col.column_name] === 'object' ? (
                                              <span className="text-xs text-gray-500 dark:text-gray-400 font-mono">
                                                {JSON.stringify(row[col.column_name]).substring(0, 50)}...
                                              </span>
                                            ) : (
                                              String(row[col.column_name])
                                            )
                                          ) : (
                                            <span className="text-xs text-gray-400 dark:text-gray-500 italic">
                                              NULL
                                            </span>
                                          )}
                                        </td>
                                      ))}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          ) : (
                            <div className="flex items-center justify-center p-8 text-gray-500 dark:text-gray-400">
                              <i className="ri-database-2-line text-2xl mr-2"></i>
                              <span>No preview data available</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                {/* Display Settings */}
                <div>
                  <div className="flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 rounded-lg mb-4 border-l-4 border-purple-500">
                    <div className="flex items-center justify-center w-10 h-10 bg-purple-500 rounded-lg">
                      <i className="ri-palette-line text-xl text-white"></i>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Display Settings</h3>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Icon
                      </label>
                      <IconPicker
                        value={formData.icon}
                        onChange={(icon) => setFormData({ ...formData, icon })}
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Min Zoom
                      </label>
                      <input
                        type="number"
                        value={formData.min_zoom}
                        onChange={(e) => setFormData({ ...formData, min_zoom: parseInt(e.target.value) })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                        min="0"
                        max="22"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Max Zoom
                      </label>
                      <input
                        type="number"
                        value={formData.max_zoom}
                        onChange={(e) => setFormData({ ...formData, max_zoom: parseInt(e.target.value) })}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                        min="0"
                        max="22"
                      />
                    </div>
                  </div>

                  {/* Clustering */}
                  <div className="mt-4 flex items-center">
                    <input
                      type="checkbox"
                      id="is_clustered"
                      checked={formData.is_clustered}
                      onChange={(e) => setFormData({ ...formData, is_clustered: e.target.checked })}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="is_clustered" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                      Enable Clustering
                    </label>
                    {formData.is_clustered && (
                      <input
                        type="number"
                        value={formData.cluster_distance}
                        onChange={(e) => setFormData({ ...formData, cluster_distance: parseInt(e.target.value) })}
                        className="ml-4 w-24 px-3 py-1 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white text-sm"
                        placeholder="Distance"
                      />
                    )}
                  </div>

                  {/* Vector Tiles */}
                  <div className="mt-4 flex items-center">
                    <input
                      type="checkbox"
                      id="vector_tile_enabled"
                      checked={formData.vector_tile_enabled}
                      onChange={(e) => setFormData({ ...formData, vector_tile_enabled: e.target.checked })}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="vector_tile_enabled" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                      Enable Vector Tiles
                    </label>
                  </div>

                  {/* Active Status */}
                  <div className="mt-4 flex items-center">
                    <input
                      type="checkbox"
                      id="is_active"
                      checked={formData.is_active}
                      onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <label htmlFor="is_active" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                      Active
                    </label>
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="mt-6 flex items-center justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => { setShowCreateModal(false); resetForm(); }}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                  {editingLayer ? 'Update Layer' : 'Create Layer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && deletingLayer && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-red-100 dark:bg-red-900/20 rounded-full">
                <i className="ri-delete-bin-line text-2xl text-red-600 dark:text-red-400"></i>
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white text-center mb-2">
                Delete Layer
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 text-center mb-6">
                Are you sure you want to delete <span className="font-semibold">{deletingLayer.layer_name}</span>? This action cannot be undone.
              </p>
              <div className="flex items-center justify-end space-x-3">
                <button
                  onClick={() => { setShowDeleteModal(false); setDeletingLayer(null); }}
                  className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDelete}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Theme Editor Modal */}
      {editingThemeLayer && (
        <LayerThemeEditor
          layer={editingThemeLayer}
          isOpen={showThemeEditor}
          onClose={() => {
            setShowThemeEditor(false);
            setEditingThemeLayer(null);
          }}
          onSave={handleSaveTheme}
        />
      )}
    </div>
  );
};

export default Layers;
